import { useCallback, useState } from 'react'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'

import { runOperation } from 'clients/exception-handling'
import type { UseBenchmarkingSetupResult } from 'features/trend-detection/features/agent-create/hooks/use-benchmarking-setup'
import { cohortFocusOverlapError } from 'features/trend-detection/features/benchmarking-agent/cohort-focus-overlap'
import {
  ResearchAgentsAction,
  trackResearchAgents,
} from 'features/trend-detection/mixpanel'
import type { TrendDetectionService } from 'features/trend-detection/service'
import { invalidateFeedQueries } from 'features/trend-detection/utils/invalidate-feed-queries'

export interface UseBenchmarkingCreateSubmitParams {
  projectId: string | null
  service: TrendDetectionService
  benchmarkingSetup: UseBenchmarkingSetupResult
}

export interface UseBenchmarkingCreateSubmitResult {
  submit: () => Promise<boolean>
  isSubmitting: boolean
}

/** Benchmarking Agent's create-and-run: the client-side cohort/focus overlap
 * pre-check, the server-side `validate-filters` round-trip, `saveAgent`,
 * then the run POST — all in one submit now that there's no intermediate
 * review step for `validateFilters` to gate. Returns whether the create
 * succeeded so the Container knows to navigate away. */
export function useBenchmarkingCreateSubmit({
  projectId,
  service,
  benchmarkingSetup,
}: UseBenchmarkingCreateSubmitParams): UseBenchmarkingCreateSubmitResult {
  const queryClient = useQueryClient()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const submit = useCallback(async (): Promise<boolean> => {
    if (!projectId || isSubmitting) return false

    const overlap = cohortFocusOverlapError(
      benchmarkingSetup.cohortFilters,
      benchmarkingSetup.focusGroupFilters,
      benchmarkingSetup.entityType,
    )
    if (overlap) {
      toast.error(overlap)
      return false
    }
    const { valid, errors } = await benchmarkingSetup.validateFilters()
    if (!valid) {
      errors.forEach((e) => toast.error(e))
      return false
    }

    setIsSubmitting(true)
    try {
      const created = await runOperation(() => benchmarkingSetup.saveAgent(), {
        operation: 'agent.benchmarking.create',
      })
      if (created.status !== 'succeeded') {
        if (created.status !== 'cancelled')
          toast.error('Failed to create benchmarking agent')
        return false
      }
      const agentUuid = created.value
      trackResearchAgents(ResearchAgentsAction.agent_create, {
        agent_type: 'benchmarking',
        agent_uuid: agentUuid,
        agent_name: benchmarkingSetup.agentName,
        entity_type: benchmarkingSetup.entityType,
      })
      const started = await runOperation(
        () => service.runBenchmarkingAgent(projectId, agentUuid),
        { operation: 'agent.benchmarking.run' },
      )
      void invalidateFeedQueries(queryClient, projectId)
      void queryClient.invalidateQueries(['benchmarking-agents', projectId])
      if (started.status !== 'succeeded') {
        toast.warn(
          'Agent created, but its run did not start. Open the agent to run it again.',
        )
        return true
      }
      trackResearchAgents(ResearchAgentsAction.agent_run, {
        agent_type: 'benchmarking',
        agent_uuid: agentUuid,
        agent_name: benchmarkingSetup.agentName,
        trigger: 'create',
      })
      toast.success('Agent created successfully!')
      return true
    } finally {
      setIsSubmitting(false)
    }
  }, [projectId, isSubmitting, benchmarkingSetup, service, queryClient])

  return { submit, isSubmitting }
}
